import { DESKTOP_MEETING_SERVICE } from 'helpers/desktopMeetingLiveSync';
import MeetingState from 'helpers/meetingState';
import { isMeetingStateTranscriptReady } from 'helpers/meetingsData';

export const getMeetingMetaStateKey = metadata =>
  metadata?.states?.meeting?.key;

const isDesktopRecordingMeeting = meeting =>
  Boolean(
    meeting &&
      (meeting.isDesktopRecording ||
        meeting.conferenceData?.service?.toLowerCase() ===
          DESKTOP_MEETING_SERVICE)
  );

/**
 * Backend `meeting_meta` publishes `audio_ready` once upload is acked. Later
 * pipeline keys imply audio is already available (e.g. we missed the exact
 * `audio_ready` event and only saw `trans_started`).
 */
export const AUDIO_PLAYBACK_READY_MEETING_STATES = [
  MeetingState.audioReady,
  MeetingState.transStarted,
  MeetingState.transReady,
  MeetingState.correctionDone
];

export const isAudioPlaybackReadyMeetingState = key =>
  AUDIO_PLAYBACK_READY_MEETING_STATES.includes(key);

/**
 * Do not replace an already-rendered transcript during an audio-only refresh.
 * Direct STT can expose valid transcript content before the audio upload ends.
 */
export const hasUsableTranscriptionContent = transcription => {
  if (transcription?.editorState) {
    return true;
  }

  const transcript = transcription?.transcript;
  if (typeof transcript !== 'string') {
    return false;
  }

  const normalized = transcript.trim();
  return normalized !== '' && normalized !== '[]';
};

/**
 * Backend contract: `meeting_meta` publishes this after audio upload is acked.
 */
export const isAudioReadyMeetingMetadata = metadata =>
  getMeetingMetaStateKey(metadata) === MeetingState.audioReady;

export const isAudioPlaybackReadyMeetingMetadata = metadata =>
  isAudioPlaybackReadyMeetingState(getMeetingMetaStateKey(metadata));

export const isTranscriptReadyMeetingMetadata = metadata =>
  isMeetingStateTranscriptReady({ states: metadata?.states });

export const didMeetingMetaBecomeAudioReady = (previousMetadata, metadata) =>
  isAudioReadyMeetingMetadata(metadata) &&
  getMeetingMetaStateKey(previousMetadata) !== MeetingState.audioReady;

export const didMeetingMetaBecomeAudioPlaybackReady = (
  previousMetadata,
  metadata
) =>
  isAudioPlaybackReadyMeetingMetadata(metadata) &&
  !isAudioPlaybackReadyMeetingMetadata(previousMetadata);

export const didMeetingMetaBecomeTranscriptReady = (
  previousMetadata,
  metadata
) =>
  isTranscriptReadyMeetingMetadata(metadata) &&
  !isTranscriptReadyMeetingMetadata(previousMetadata);

export const didMeetingBecomeAudioReady = (previousMeeting, meeting) => {
  const previousKey = previousMeeting?.states?.meeting?.key;
  const currentKey = meeting?.states?.meeting?.key;

  return (
    currentKey === MeetingState.audioReady &&
    previousKey !== MeetingState.audioReady
  );
};

export const didMeetingBecomeAudioPlaybackReady = (
  previousMeeting,
  meeting
) => {
  const previousKey = previousMeeting?.states?.meeting?.key;
  const currentKey = meeting?.states?.meeting?.key;

  return (
    isAudioPlaybackReadyMeetingState(currentKey) &&
    !isAudioPlaybackReadyMeetingState(previousKey)
  );
};

export const didMeetingBecomeTranscriptReady = (previousMeeting, meeting) =>
  isMeetingStateTranscriptReady(meeting) &&
  !isMeetingStateTranscriptReady(previousMeeting);

/**
 * Recording URL often lands on the meeting after `audio_ready` with no further
 * meeting-state transition. Detect that so the audio player can load media.
 */
export const didMeetingRecordingUrlBecomeAvailable = (
  previousMeeting,
  meeting
) => Boolean(meeting?.recording) && !previousMeeting?.recording;

export const shouldRefreshMeetingContentAfterMetaUpdate = (
  previousMetadata,
  metadata
) =>
  didMeetingMetaBecomeAudioPlaybackReady(previousMetadata, metadata) ||
  didMeetingMetaBecomeTranscriptReady(previousMetadata, metadata);

/**
 * True when desktop post-processing has signaled that playback media should
 * exist (socket `meeting_meta` / meeting.states), but Redux still lacks the
 * recording file. Used for one-shot reconcile — not continuous polling.
 *
 * Prefer / reconcile both sources: on route mount or after a missed websocket
 * frame the meeting object can still hold an older key (e.g.
 * bot_post_processing_start) while the metadata slice is already
 * audio_ready. Taking meeting.states alone would skip the recovery path.
 */
export const shouldReconcileDesktopAudioPlayer = (
  meeting,
  {
    hasRecordingMedia = false,
    meetingMetadata = null,
    isDesktopRecording = false
  } = {}
) => {
  if (!isDesktopRecording && !isDesktopRecordingMeeting(meeting)) {
    return false;
  }

  if (hasRecordingMedia) {
    return false;
  }

  const metadataStateKey = getMeetingMetaStateKey(meetingMetadata);
  const meetingStateKey = meeting?.states?.meeting?.key;

  return (
    isAudioPlaybackReadyMeetingState(metadataStateKey) ||
    isAudioPlaybackReadyMeetingState(meetingStateKey)
  );
};
